/* PLW-027 navigation: mobile drawer + desktop mega menu. No dependencies. */
(() => {
  if (customElements.get('plw-drawer')) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  class PlwDrawer extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.plw-drawer__open');
      this.panel = this.querySelector('.plw-drawer__panel');
      this.overlay = this.querySelector('.plw-drawer__overlay');
      if (!this.button || !this.panel) return;

      // Portal to <body> so the drawer sits above sticky header stacking contexts and app widgets.
      document.body.appendChild(this.overlay);
      document.body.appendChild(this.panel);

      this.onKeydown = this.onKeydown.bind(this);
      this.button.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.overlay.addEventListener('click', () => this.close());
      this.panel.querySelectorAll('[data-plw-close]').forEach((el) => el.addEventListener('click', () => this.close()));

      // Close the drawer when the viewport grows to desktop.
      this.mql = window.matchMedia('(min-width: 990px)');
      this.onMql = (e) => e.matches && this.isOpen && this.close(false);
      this.mql.addEventListener('change', this.onMql);

      // Single-open accordion fallback for browsers without <details name>.
      this.panel.querySelectorAll('details.plw-acc').forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          this.panel.querySelectorAll('details.plw-acc[open]').forEach((o) => o !== d && (o.open = false));
          const top = d.getBoundingClientRect().top;
          const bodyTop = this.panel.querySelector('.plw-drawer__body').getBoundingClientRect().top;
          if (top < bodyTop) d.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
      });
    }

    disconnectedCallback() {
      // Theme editor re-render: remove portaled nodes.
      this.overlay?.remove();
      this.panel?.remove();
      this.mql?.removeEventListener('change', this.onMql);
      document.documentElement.classList.remove('plw-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    open() {
      this.lastFocus = document.activeElement;
      this.overlay.hidden = false;
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('plw-drawer-open');
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-visible');
        this.panel.classList.add('is-open');
      });
      this.panel.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKeydown);
    }

    close(restoreFocus = true) {
      this.button.setAttribute('aria-expanded', 'false');
      this.overlay.classList.remove('is-visible');
      this.panel.classList.remove('is-open');
      document.documentElement.classList.remove('plw-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
      const ps = this.panel.querySelector('predictive-search');
      if (ps && typeof ps.close === 'function') ps.close(true);
      const done = () => {
        if (this.isOpen) return;
        this.overlay.hidden = true;
        this.panel.hidden = true;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 280);
      if (restoreFocus) (this.lastFocus && this.lastFocus.focus ? this.lastFocus : this.button).focus({ preventScroll: true });
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        const ps = this.panel.querySelector('predictive-search[open]');
        if (ps) return; // let predictive search close its results first
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('plw-drawer', PlwDrawer);

  /* ---------- Desktop mega menu ---------- */
  const OPEN_DELAY = 220;
  const CLOSE_DELAY = 380;
  const hoverMql = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  function initMega(root) {
    const items = [...root.querySelectorAll('[data-plw-mega]')];
    if (!items.length || root.dataset.plwMegaReady) return;
    root.dataset.plwMegaReady = 'true';
    const scrim = document.querySelector('[data-plw-mega-scrim]');
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    const setHeaderBottom = () => {
      const wrap = root.closest('.header-wrapper') || root;
      document.documentElement.style.setProperty('--plw-header-bottom', `${Math.max(0, wrap.getBoundingClientRect().bottom)}px`);
    };

    const panelOf = (item) => item.querySelector('.plw-mega');
    const btnOf = (item) => item.querySelector('.plw-mnav__toggle');

    const close = (item, focusBtn = false) => {
      if (!item) return;
      btnOf(item)?.setAttribute('aria-expanded', 'false');
      const panel = panelOf(item);
      if (panel) panel.hidden = true;
      item.classList.remove('is-open');
      if (openItem === item) openItem = null;
      if (!openItem && scrim) scrim.hidden = true;
      if (focusBtn) btnOf(item)?.focus();
    };

    const open = (item) => {
      if (openItem && openItem !== item) close(openItem);
      setHeaderBottom();
      btnOf(item)?.setAttribute('aria-expanded', 'true');
      const panel = panelOf(item);
      if (panel) panel.hidden = false;
      item.classList.add('is-open');
      openItem = item;
      if (scrim && !item.classList.contains('plw-mnav__item--compact')) scrim.hidden = false;
    };

    items.forEach((item) => {
      const btn = btnOf(item);
      if (!btn) return;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        btn.getAttribute('aria-expanded') === 'true' ? close(item) : open(item);
      });

      item.addEventListener('mouseenter', () => {
        if (!hoverMql.matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        // Switch instantly when another panel is already open; otherwise wait for intent.
        openTimer = setTimeout(() => open(item), openItem ? 0 : OPEN_DELAY);
      });

      item.addEventListener('mouseleave', () => {
        if (!hoverMql.matches) return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => close(item), CLOSE_DELAY);
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          e.preventDefault();
          close(item, true);
        }
      });

      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close(item);
      });
    });

    document.addEventListener('click', (e) => {
      if (openItem && !openItem.contains(e.target)) close(openItem);
    });
    scrim?.addEventListener('mouseenter', () => {
      clearTimeout(openTimer);
      if (openItem) closeTimer = setTimeout(() => openItem && close(openItem), 120);
    });
    window.addEventListener('scroll', () => openItem && setHeaderBottom(), { passive: true });

    // Warm panel images on first pointer/keyboard intent so panels open with images ready.
    const warm = () => root.querySelectorAll('.plw-mega img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    root.addEventListener('pointerenter', warm, { once: true });
    root.addEventListener('focusin', warm, { once: true });
  }

  const boot = () => document.querySelectorAll('.plw-mnav').forEach(initMega);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
